import { Badge, Button, Card, Col, Container, Row } from 'react-bootstrap';
import { BsChat, BsEnvelope, BsGeoAlt, BsTelephone, BsWhatsapp } from 'react-icons/bs';
import { FaFacebookSquare, FaInstagram, FaTwitterSquare, FaYoutube } from 'react-icons/fa';
import { FaLinkedin } from 'react-icons/fa6';
import { getHomeIcon } from '@/views/home/components/homeIconMap';
import { resolveSiteBuilderIconComponent } from '@/utils/siteBuilderIconMap';

const HELP_WHATSAPP_NUMBER = '+30 690 6241516';
const HELP_WHATSAPP_URL = 'https://wa.me/306906241516';
const HELP_PHONE_NUMBER = '+30 28420 22892';
const HELP_PHONE_URL = 'tel:+302842022892';

const getContactCardFallbackIcon = (title = '') => {
  const normalized = String(title || '').toLowerCase();
  if (normalized.includes('call') || normalized.includes('phone') || normalized.includes('whatsapp')) {
    return BsTelephone;
  }
  if (normalized.includes('visit') || normalized.includes('office') || normalized.includes('address')) {
    return BsGeoAlt;
  }
  if (normalized.includes('mail') || normalized.includes('email')) {
    return BsEnvelope;
  }
  if (normalized.includes('connect') || normalized.includes('social') || normalized.includes('follow')) {
    return BsChat;
  }
  return null;
};

const resolveActionIcon = (action) => {
  if (!action) return null;
  return (
    action.IconComponent ||
    resolveSiteBuilderIconComponent(action.icon) ||
    getHomeIcon(action.icon) ||
    null
  );
};

const resolveSocialIcon = (action, href, label) => {
  const explicit = resolveActionIcon(action);
  if (explicit) return explicit;
  const probe = `${href || ''} ${label || ''} ${action?.icon || ''}`.toLowerCase();
  if (probe.includes('facebook')) return FaFacebookSquare;
  if (probe.includes('instagram')) return FaInstagram;
  if (probe.includes('linkedin')) return FaLinkedin;
  if (probe.includes('youtube')) return FaYoutube;
  if (probe.includes('twitter') || probe.includes('x.com')) return FaTwitterSquare;
  if (probe.includes('whatsapp') || probe.includes('wa.me')) return BsWhatsapp;
  return BsChat;
};

const renderAction = (action, idx) => {
  const href = action?.href || action?.url || action?.link || '';
  if (!action || !href) return null;
  const Icon = resolveActionIcon(action);
  const label = action.label || href;
  const variant = action.variant || 'primary';
  const style = action.style || 'button';

  if (style === 'social') {
    const SocialIcon = resolveSocialIcon(action, href, label);
    return (
      <a
        key={`${href}-${idx}`}
        className={`btn btn-sm ${action.className || ''} text-white flex-centered`}
        href={href}
        target="_blank"
        rel="noreferrer"
        aria-label={action.label || 'Social link'}
      >
        <SocialIcon className="fa-fw" />
      </a>
    );
  }

  if (style === 'link') {
    return (
      <a
        key={`${href}-${idx}`}
        href={href}
        className="btn btn-link text-decoration-underline p-0 d-inline-flex align-items-center gap-2"
      >
        {Icon && <Icon />}
        {label}
      </a>
    );
  }

  return (
    <Button
      key={`${href}-${idx}`}
      size="sm"
      variant={variant}
      as="a"
      href={href}
      className="d-inline-flex align-items-center gap-2"
    >
      {Icon && <Icon />}
      {label}
    </Button>
  );
};

const ContactCards = ({ sectionData }) => {
  const badgeText = sectionData?.section_name?.trim() || 'Contact';
  const titleText = sectionData?.title?.trim() || 'Connect with our team';
  const introText = sectionData?.intro_text ?? '';
  const items = Array.isArray(sectionData?.items) ? sectionData.items : [];

  const bgColor = sectionData?.content?.background_color || sectionData?.background_color || 'light';
  const bgEffect = sectionData?.content?.background_effect || sectionData?.background_effect || null;
  let bgClass = bgColor === 'primary' ? 'section-bg-primary' : bgColor === 'white' ? 'bg-white' : bgColor === 'dark' ? 'bg-dark text-white' : 'bg-light';
  if (bgColor === 'primary' && bgEffect === 'gradient') bgClass = 'section-bg-primary-gradient';
  const hasDotsEffect = bgColor === 'primary' && bgEffect === 'dots';
  const useDbBackground = !!(sectionData?.content?.background_color || sectionData?.background_color);

  return (
    <section className={`contact-info-section py-5 py-lg-6 ${bgClass} ${hasDotsEffect ? 'overflow-hidden position-relative' : ''}`} {...(useDbBackground && { 'data-bg-from-db': 'true' })}>
      {hasDotsEffect && (
        <>
          <span className="section-bg-dots section-bg-dots--left d-none d-xl-block" />
          <span className="section-bg-dots section-bg-dots--right d-none d-lg-block" />
        </>
      )}
      <Container>
        <Row className="mb-4">
          <Col xs={12}>
            <Badge bg="primary" className="bg-opacity-10 text-primary px-3 py-2 mb-3">
              {badgeText}
            </Badge>
            <h2 className="sec-title mb-3">{titleText}</h2>
            {introText && (
              <p className="intro-text mb-0">
                <span dangerouslySetInnerHTML={{ __html: introText }} />
              </p>
            )}
          </Col>
        </Row>
        <Row className="g-4">
          {items.map((item) => {
            if (!(item.is_active === 1 || item.is_active === '1' || item.is_active === true || item.is_active === undefined)) {
              return null;
            }
            const Icon = item.IconComponent
              || resolveSiteBuilderIconComponent(item.icon)
              || getHomeIcon(item.icon)
              || getContactCardFallbackIcon(item.title);
            const iconColor = item.icon_color?.trim() ? item.icon_color : 'primary';
            const actions = Array.isArray(item?.content?.actions) ? item.content.actions : [];
            const normalizedTitle = String(item?.title || '').trim().toLowerCase();
            const isCallUsCard = normalizedTitle.includes('call us');
            const finalActions = isCallUsCard
              ? [
                  { href: HELP_PHONE_URL, label: HELP_PHONE_NUMBER, style: 'link', IconComponent: BsTelephone },
                  { href: HELP_WHATSAPP_URL, label: HELP_WHATSAPP_NUMBER, style: 'link', IconComponent: BsWhatsapp },
                ]
              : actions;
            const finalDescription = isCallUsCard
              ? 'Call or WhatsApp our operations team using the direct numbers below.'
              : item.description;

            return (
              <Col key={item.id} md={6} xl={4}>
                <Card className="contact-info-card h-100">
                  <Card.Body className="d-flex flex-column gap-3">
                    <div className={`contact-info-card__icon bg-${iconColor} bg-opacity-10 text-${iconColor}`}>
                      {Icon && <Icon size={20} />}
                    </div>
                    <div>
                      <h5 className="mb-2">{item.title}</h5>
                      {finalDescription && (
                        <p className="text-muted mb-0">
                          <span dangerouslySetInnerHTML={{ __html: finalDescription }} />
                        </p>
                      )}
                    </div>
                    {finalActions.length > 0 && (
                      <div className={`contact-info-card__actions ${isCallUsCard ? 'contact-info-card__actions--stack' : ''}`}>
                        {finalActions.map(renderAction)}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Container>
    </section>
  );
};

export default ContactCards;
